import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import BoxListItem from '../components/BoxListItem';
import ErrorHandler from '../components/ErrorHandler';
import Loader from '../components/Loader';
import ScanOrBoxInfoHeader from '../components/ScanOrBoxInfoHeader';
import { TOAST_TYPE_MAP, ToastCustom } from '../components/ToastCustom';
import { dayjs } from '../helpers/DateTime';
import { getDeleteItemModalContent } from '../helpers/ModalContentHelpers';
import { downloadScanDocument } from '../helpers/ScanningHelpers';
import { isNullable } from '../helpers/TypingHelpers';
import { getUserSettings } from '../helpers/UserHelpers';
import { getCompleteScanOrBoxContent } from '../modals/content/CompleteScanOrBox';
import GenericModal from '../modals/GenericModal';
import { useValidateAndCompleteScanOrBox } from '../services/hooks/useCompleteScanOrBox';
import { useCreateBox } from '../services/hooks/useCreateBox';
import { useGetLocations } from '../services/hooks/useGetLocations';
import { getScanQueryKey, useGetScan } from '../services/hooks/useGetScan';
import { deleteBoxById } from '../services/Scans';
import { BoxRes } from '../types/Box';
import { Location } from '../types/Location';
import { GenericModalData } from '../types/Modal';
import { ScanActionType } from '../types/Scan';
import { UserSettings } from '../types/UserSettings';
import ScanCreateEditModal from './ScanCreateEditModal';
import { ScanProps } from './ScansHandler';

const BoxScanOverview = ({ currentUser }: ScanProps): ReactElement => {
    const { scanId, boxId } = useParams<{ scanId: string; boxId: string }>();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [isLoadingDeleteForIds, setIsLoadingDeleteForIds] = useState<Array<number>>([]);
    const [settings] = useState<UserSettings | null>(getUserSettings());
    const [boxes, setBoxes] = useState<Array<BoxRes>>([]);
    const [modalData, setModalData] = useState<GenericModalData | null>(null);
    const [boxFoundInProgress, setBoxFoundInProgress] = useState(false);
    const [showCreateEditModal, setShowCreateEditModal] = useState(false);

    const { data: scanRes, isLoading: isLoadingScan } = useGetScan({ scanId: scanId, onError: ErrorHandler });
    const { locations } = useGetLocations();

    const { mutate: createBoxMutation, isPending: isCreateBoxPending } = useCreateBox();

    const { mutate: completeScanMutation, isPending: isPendingCompletion } = useValidateAndCompleteScanOrBox(
        () => {
            if (settings?.automaticallyDownloadXlsOnScanCompletion && scanRes) {
                downloadScanDocument('excel', 'xlsx', scanRes);
            }
            ToastCustom(`Scan ${scanId} completed`, TOAST_TYPE_MAP.SUCCESS);
            navigate('/scanning');
        },
        () => ErrorHandler(undefined, 'Completing scan failed. Please contact customer support')
    );

    const { mutate: deleteBoxMutation, isPending: isDeletingBoxPending } = useMutation({
        mutationFn: deleteBoxById,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: getScanQueryKey(data.scanId) });
            queryClient.invalidateQueries({ queryKey: ['scans'] });

            const boxIdDeleted = variables.boxId;
            setBoxes(boxes.filter((box) => box.id !== boxIdDeleted));
            ToastCustom(`Box ${boxIdDeleted} successfully deleted`, TOAST_TYPE_MAP.SUCCESS);
        },
        onError: (err) => ErrorHandler(err),
        onSettled: (_, __, variables) =>
            setIsLoadingDeleteForIds((prevState) => prevState.filter((id) => id !== variables.boxId))
    });

    const scanBelongsToUserLocation = useMemo(() => {
        if (scanRes) {
            return scanRes.locationName === currentUser.locationName;
        }
        return false;
    }, [currentUser, scanRes]);

    useEffect(() => {
        if (scanRes) {
            setBoxes([...scanRes.boxes].sort((a, b) => dayjs(b.createdTime).diff(dayjs(a.createdTime))));
        }
    }, [scanRes]);

    useEffect(() => {
        setBoxFoundInProgress(anyBoxInProgress(boxes));
    }, [boxes]);

    const openCompleteBoxScanModal = () => {
        if (scanRes) {
            setModalData({
                ...getCompleteScanOrBoxContent({
                    scan: scanRes
                }),
                confirmAction: () => completeScanMutation({ scanId: scanRes.id.toString() }),
                closeMe: () => setModalData(null)
            });
        }
    };

    const openDeleteBoxModal = (boxId: number) => {
        if (scanRes) {
            setModalData({
                ...getDeleteItemModalContent({ id: boxId, type: 'BOX' }),
                confirmAction: () => {
                    deleteBoxMutation({ scanId: scanRes.id, boxId: boxId });
                    setModalData(null);
                },
                closeMe: () => setModalData(null)
            });
        }
    };

    const handleScanAction = (event: ScanActionType) => {
        switch (event) {
            case 'COMPLETE':
                openCompleteBoxScanModal();
                break;
            case 'EDIT':
                setShowCreateEditModal(true);
                break;
            case 'ADD':
                if (!scanBelongsToUserLocation) {
                    ToastCustom('Not available on a remote location', TOAST_TYPE_MAP.WARNING);
                } else if (scanRes) {
                    createBoxMutation({ scanId: scanRes.id });
                }
                break;
            default:
                break;
        }
    };

    const isCompleteBtnDisabled = boxFoundInProgress || boxes?.length === 0 || !scanBelongsToUserLocation;

    return (
        <div style={{ position: 'relative', minHeight: '80vh' }}>
            <Loader size={20} loading={isLoadingScan} overComponent={true} />

            {scanRes && (
                <>
                    <ScanOrBoxInfoHeader
                        scanItem={scanRes}
                        scanItemMeta={{
                            scanId: scanRes.id,
                            destinationName: scanRes.destinationName,
                            trackingId: scanRes.trackingId,
                            salesOrderId: scanRes.salesOrderId
                        }}
                        currentUser={currentUser}
                        buttonsState={{
                            COMPLETE: {
                                isDisabled: isCompleteBtnDisabled,
                                tooltipMsg: getCompleteBtnDataTip(boxFoundInProgress, scanBelongsToUserLocation),
                                isLoading: isPendingCompletion,
                                isVisible: scanRes.statusName === 'IN_PROGRESS' && !scanRes?.deletedByUsername
                            },
                            ACTIONS: {
                                isDisabled: isCreateBoxPending,
                                isVisible: isNullable(scanRes.deletedByUsername)
                            }
                        }}
                        onAction={handleScanAction}
                        isBoxOverview={scanRes?.isBoxScan && !boxId}
                    />

                    <div style={{ marginTop: '30px' }}>
                        <strong>
                            Total boxes added: {boxes.length} - Total loggers added: {getTotalLoggerCount(boxes)}
                        </strong>

                        {boxes.map((box) => (
                            <BoxListItem
                                scanBelongsToUserLocation={scanBelongsToUserLocation}
                                key={box.id}
                                scan={scanRes}
                                box={box}
                                isDeleting={isDeletingBoxPending && isLoadingDeleteForIds.includes(box.id)}
                                deleteBox={(boxId) => {
                                    openDeleteBoxModal(boxId);
                                    setIsLoadingDeleteForIds((prevState) => [...prevState, boxId]);
                                }}
                            />
                        ))}
                    </div>
                </>
            )}

            <GenericModal modalData={modalData} />

            {showCreateEditModal && (
                <ScanCreateEditModal
                    currentUser={currentUser}
                    scanToEdit={scanRes}
                    destinations={locations?.map((location: Location) => location.name) ?? []}
                    closeMe={() => {
                        setShowCreateEditModal(false);
                    }}
                />
            )}
        </div>
    );
};

const getCompleteBtnDataTip = (boxFoundInProgress: boolean, scanBelongsToUserLocation: boolean) => {
    if (boxFoundInProgress && scanBelongsToUserLocation) {
        return 'All boxes need to be completed in order to complete the scan';
    } else if (!scanBelongsToUserLocation) {
        return 'Not available on a remote location';
    }
    return '';
};

const anyBoxInProgress = (_boxes: Array<BoxRes>) => {
    for (const box of _boxes) {
        if (box.statusName === 'IN_PROGRESS') {
            return true;
        }
    }
    return false;
};

const getTotalLoggerCount = (b: Array<BoxRes>) => b.map((e) => e.loggerCount).reduce((c, d) => c + d, 0);

export default BoxScanOverview;
